// [LLDBG] TEMP on-screen readout — remove with the rest of the [LLDBG] instrumentation.
//
// Renders the values already captured by the existing lldbg() probes (see
// src/lib/lldbg.ts) as a small fixed-position monospace panel, so an on-device
// interview run can SCREENSHOT them. It adds NO new instrumentation and touches
// NO audio logic — it only subscribes to the sink's ring buffer.
//
// Gated behind ?debug=1: renders null for real subjects. Add the query param
// manually when running an on-device test.
//
// PII: the probes surfaced here carry booleans / enums / numbers only, but as a
// defence-in-depth measure any field whose key looks identifying (name, dob,
// email, slug, token, transcript, tribute/interview id, the prompt URL, the
// question id) is dropped before render. NB: "state" is deliberately NOT
// redacted — the surfaced fields are audioContextState / readyState /
// networkState (audio machine states), never the US-state gate value.
import { useSyncExternalStore, useState } from "react";
import { createPortal } from "react-dom";
import { subscribeLldbg, getLldbgSnapshot, type LldbgEvent } from "@/lib/lldbg";

// The probe tags most relevant to C1/C2/C3, in the order they matter for
// diagnosis. Latest value per tag is shown even if it scrolled out of "recent".
const PRIORITY_TAGS = [
  "playPrompt",
  "promptAudio:attempt",
  "promptAudio:playing",
  "promptAudio:rejected",
  "promptAudio:fallbackSpeech",
  "skip:tap",
  "skip:branch",
  "record:stop",
  "answer",
] as const;

// Drop keys that could carry PII / identifiers. Substring, case-insensitive.
// promptKey = prompt URL (may embed slug/tribute path); questionId = row id.
const REDACT_KEY = /name|dob|email|slug|token|transcript|tribute|interview|promptkey|questionid/i;

const MAX_VALUE_LEN = 60; // keep long error messages from blowing out the panel

function isDebugEnabled(): boolean {
  try {
    return typeof window !== "undefined" && window.location.search.includes("debug=1");
  } catch {
    return false;
  }
}

function formatValue(v: unknown): string {
  const s = typeof v === "string" ? v : JSON.stringify(v);
  if (s == null) return String(v);
  return s.length > MAX_VALUE_LEN ? s.slice(0, MAX_VALUE_LEN) + "…" : s;
}

function formatData(data: Record<string, unknown>): string {
  const parts = Object.entries(data)
    .filter(([k]) => !REDACT_KEY.test(k))
    .map(([k, v]) => `${k}=${formatValue(v)}`);
  return parts.length ? parts.join("  ") : "(no fields)";
}

export default function LldbgOverlay() {
  // Hooks run unconditionally (before the debug gate) to keep hook order stable.
  const events = useSyncExternalStore(subscribeLldbg, getLldbgSnapshot, getLldbgSnapshot);
  const [collapsed, setCollapsed] = useState(false);

  if (!isDebugEnabled()) return null;
  // Portal target must exist (always does client-side after mount).
  if (typeof document === "undefined" || !document.body) return null;

  // Latest event per tag (events are in emit order; later overwrites earlier).
  const latest = new Map<string, LldbgEvent>();
  for (const e of events) latest.set(e.tag, e);

  // Last handful, newest first, for ordering context (e.g. skip:tap → playPrompt).
  // 13 priority rows now, so keep the recent tail short to fit a phone screenshot.
  const recent = events.slice(-6).reverse();

  // Portalled to document.body so no transformed/filtered ancestor in the interview
  // subtree can become its containing block and pull it off the viewport corner.
  // The component still lives in QuestionFlow's tree (mounts/unmounts with it) and
  // still reads the module-level lldbg ring buffer via useSyncExternalStore — the
  // portal only relocates the rendered DOM node, not the subscription.
  // Anchored to TOP (mobile Safari's bottom toolbar offsets bottom-fixed elements)
  // with safe-area insets so it clears the notch / Dynamic Island; maxHeight is
  // computed from the top anchor so it can never run off the bottom of the screen.
  return createPortal(
    <div
      style={{
        position: "fixed",
        top: "calc(8px + env(safe-area-inset-top, 0px))",
        right: "calc(8px + env(safe-area-inset-right, 0px))",
        zIndex: 2147483647,
        maxWidth: "min(94vw, 520px)",
        maxHeight: "calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 16px)",
        overflow: "auto",
        background: "rgba(0,0,0,0.88)",
        color: "#e6e6e6",
        border: "1px solid #444",
        borderRadius: 8,
        padding: 7,
        font: "10px/1.3 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
        boxShadow: "0 2px 10px rgba(0,0,0,0.5)",
        pointerEvents: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          marginBottom: collapsed ? 0 : 6,
        }}
      >
        <strong style={{ color: "#7fd" }}>[LLDBG] {events.length ? `#${events[events.length - 1].seq}` : "—"}</strong>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          style={{
            background: "#222",
            color: "#e6e6e6",
            border: "1px solid #555",
            borderRadius: 4,
            padding: "2px 8px",
            font: "inherit",
            cursor: "pointer",
          }}
        >
          {collapsed ? "show" : "hide"}
        </button>
      </div>

      {collapsed ? null : (
        <>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <tbody>
              {PRIORITY_TAGS.map((tag) => {
                const e = latest.get(tag);
                return (
                  <tr key={tag} style={{ verticalAlign: "top" }}>
                    <td
                      style={{
                        color: e ? "#7fd" : "#666",
                        whiteSpace: "nowrap",
                        paddingRight: 8,
                        paddingBottom: 1,
                      }}
                    >
                      {tag}
                    </td>
                    <td style={{ color: e ? "#e6e6e6" : "#666", wordBreak: "break-word", paddingBottom: 2 }}>
                      {e ? formatData(e.data) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{ marginTop: 6, borderTop: "1px solid #333", paddingTop: 4, color: "#999" }}>
            recent (newest first):
          </div>
          {recent.length ? (
            recent.map((e) => (
              <div key={e.seq} style={{ wordBreak: "break-word", paddingTop: 2 }}>
                <span style={{ color: "#888" }}>#{e.seq}</span>{" "}
                <span style={{ color: "#7fd" }}>{e.tag}</span>{" "}
                <span>{formatData(e.data)}</span>
              </div>
            ))
          ) : (
            <div style={{ color: "#666", paddingTop: 2 }}>no probes fired yet</div>
          )}
        </>
      )}
    </div>,
    document.body,
  );
}
